import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Tuple } from "@/__main__/data-model.mjs";
import { RANK_SYMBOLS } from "@/game-lol/constants/constants-ranks.mjs";
import { ROLE_SYMBOLS } from "@/game-lol/utils/roles.mjs";
import QueueSymbol from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";

const COLUMN_COUNT = 19;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, () => String);

const buildModel = Tuple(COLUMNS) satisfies ModelDefinition;

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const SynergyResult = {
  patch: String,
  queue_id: QueueSymbol,
  tier: RankSymbol,
  synergy_tier: Number,
  wins: Number,
  games: Number,
  win_rate: Number,
  win_rate_delta: Number,
  champion_id: Number,
  champion_num_games: Number,
  individual_position: RoleSymbol,
  ally_champion_id: Number,
  ally_champion_num_games: Number,
  ally_individual_position: RoleSymbol,
} satisfies ModelDefinition;

export type Synergy = FromModel<typeof SynergyResult>;

const SynergiesModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Number,
          dataArray: [buildModel],
          rowCount: Number,
          rowOffset: Number,
        },
      },
    },
  },
}).transform([SynergyResult], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const synergies = list.map((duo) => {
    const result = {
      patch: "",
      queue_id: null,
      tier: RANK_SYMBOLS.emeraldPlus,
      synergy_tier: 0,
      wins: 0,
      games: 0,
      win_rate: 0,
      win_rate_delta: 0,
      champion_id: 0,
      champion_num_games: 0,
      individual_position: ROLE_SYMBOLS.all,
      ally_champion_id: 0,
      ally_champion_num_games: 0,
      ally_individual_position: ROLE_SYMBOLS.all,
    };

    for (const column of columns) {
      const value = duo[column.position] as unknown as string;
      const type = column.typeName;
      result[column.name] = castType(type, value);
    }

    return result;
  });

  return synergies;
});

export default SynergiesModel;

function castType(type: string, value: string) {
  switch (type) {
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
      return Number(value);
    case "STRING":
    case undefined:
    default:
      return value;
  }
}
